"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, MapPin, X } from "lucide-react";

interface GalleryItem {
  image: string;
  title: string;
  location: string;
  service: string;
}

export default function Gallery({ images }: { images: GalleryItem[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<GalleryItem | null>(null);

  useEffect(() => {
    if (!preview) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPreview(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [preview]);

  const scroll = (direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -380 : 380,
      behavior: "smooth",
    });
  };

  return (
    <section className="relative overflow-hidden bg-[#0C4A6E] px-5 py-20 text-white sm:px-8 md:py-28 lg:px-10">
      <div className="absolute -right-36 -top-20 h-96 w-96 rounded-full bg-[#0369A1]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-3 text-xs font-extrabold tracking-[0.18em] text-[#7DD3FC] uppercase">
              Recent installations
            </p>
            <h2 className="max-w-2xl text-4xl leading-tight font-extrabold tracking-[-0.035em] sm:text-5xl">
              Real work. Real Bengaluru homes.
            </h2>
            <p className="mt-4 max-w-xl leading-7 text-white/65">
              Explore completed installations and tap any project for a closer look.
            </p>
          </div>

          <div className="hidden gap-3 sm:flex">
            <button
              onClick={() => scroll("left")}
              className="grid size-12 place-items-center rounded-full border border-white/18 bg-white/8 text-white transition hover:bg-white hover:text-[#0C4A6E]"
              aria-label="Scroll projects left"
            >
              <ArrowLeft className="size-5" />
            </button>
            <button
              onClick={() => scroll("right")}
              className="grid size-12 place-items-center rounded-full bg-[#0369A1] text-white transition hover:bg-[#075985]"
              aria-label="Scroll projects right"
            >
              <ArrowRight className="size-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-4 sm:-mx-8 sm:px-8 lg:-mx-0 lg:px-0"
        >
          {images.map((item) => (
            <button
              key={`${item.title}-${item.location}`}
              onClick={() => setPreview(item)}
              className="group relative aspect-[4/5] min-w-[82vw] snap-start overflow-hidden rounded-[1.6rem] bg-[#082F49] text-left sm:min-w-[340px] md:min-w-[370px]"
              aria-label={`View ${item.title}`}
            >
              <Image
                src={item.image}
                alt={`${item.service} in ${item.location}, Bengaluru`}
                fill
                sizes="(max-width: 640px) 82vw, 370px"
                className="object-cover transition duration-500 group-hover:scale-[1.035]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#082F49] via-[#082F49]/5 to-transparent" />

              <div className="absolute left-4 top-4 rounded-full bg-[#FFFFFF] px-3 py-1.5 text-[10px] font-extrabold tracking-wide text-[#0C4A6E] uppercase">
                {item.service}
              </div>

              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <h3 className="text-xl font-extrabold text-white">{item.title}</h3>
                <p className="mt-2 flex items-center gap-2 text-sm text-white/65">
                  <MapPin className="size-4 text-[#7DD3FC]" />
                  {item.location}, Bengaluru
                </p>
              </div>
            </button>
          ))}
        </div>

        <p className="mt-4 text-center text-xs font-semibold tracking-wide text-white/70 sm:hidden">
          Swipe to explore projects →
        </p>
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[80] grid place-items-center bg-[#020617]/92 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label={preview.title}
          onClick={() => setPreview(null)}
        >
          <div
            className="relative h-[78vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-[#082F49]"
            onClick={(event) => event.stopPropagation()}
          >
            <Image
              src={preview.image}
              alt={preview.title}
              fill
              sizes="95vw"
              className="object-contain"
            />
            <button
              onClick={() => setPreview(null)}
              className="absolute right-4 top-4 grid size-11 place-items-center rounded-full bg-white text-[#0C4A6E] shadow-lg"
              aria-label="Close project preview"
            >
              <X className="size-5" />
            </button>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#082F49] to-transparent p-6 pt-20">
              <h3 className="text-xl font-extrabold text-white">{preview.title}</h3>
              <p className="mt-1 text-sm text-white/70">
                {preview.service} in {preview.location}, Bengaluru
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
